import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import ActionButtonOverlay from '../../components/navigation/ActionButtonOverlay';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AppointmentForm from './components/AppointmentForm';
import AppointmentCard from './components/AppointmentCard';
import AppointmentCalendarView from './components/AppointmentCalendarView';
import appointmentService from '../../services/appointmentService';
import { useAuth } from '../../contexts/AuthContext';

const DoctorAppointments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [prefillNotes, setPrefillNotes] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [prefillDate, setPrefillDate] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadAppointments();
      loadPrefillNotes();
    }
  }, [user?.id]);

  const loadAppointments = async () => {
    setLoading(true);
    const { data, error: fetchError } = await appointmentService?.getAppointments(user?.id);
    if (fetchError) {
      setError('Failed to load appointments. Please try again.');
    } else {
      setAppointments(data || []);
    }
    setLoading(false);
  };

  const loadPrefillNotes = async () => {
    const notes = await appointmentService?.getLatestSymptomNotes(user?.id);
    setPrefillNotes(notes);
  };

  const handleAddAppointment = async (formData) => {
    if (appointments?.length >= 3 && !editingAppointment) {
      setError('You can have a maximum of 3 upcoming appointments. Please delete one to add another.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      if (editingAppointment) {
        const { error: updateError } = await appointmentService?.updateAppointment(
          editingAppointment?.id, user?.id, formData
        );
        if (updateError) throw updateError;
        setSuccessMessage('Appointment updated successfully!');
      } else {
        const { error: createError } = await appointmentService?.createAppointment(user?.id, formData);
        if (createError) throw createError;
        setSuccessMessage('Appointment added! You will receive email reminders 7 days, 1 day, and 2 hours before.');
      }
      await loadAppointments();
      setShowForm(false);
      setEditingAppointment(null);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err?.message || 'Failed to save appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditWithData = (appointment) => {
    const apptDate = new Date(appointment?.appointment_date);
    const localDatetime = new Date(apptDate.getTime() - apptDate.getTimezoneOffset() * 60000)?.toISOString()?.slice(0, 16);
    setEditingAppointment(appointment);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setIsSubmitting(true);
    const { error: deleteError } = await appointmentService?.deleteAppointment(deleteConfirmId, user?.id);
    if (deleteError) {
      setError('Failed to delete appointment.');
    } else {
      setSuccessMessage('Appointment deleted.');
      await loadAppointments();
      setTimeout(() => setSuccessMessage(''), 3000);
    }
    setDeleteConfirmId(null);
    setIsSubmitting(false);
  };

  const handleAddForDate = (dateStr) => {
    setPrefillDate(dateStr);
    setEditingAppointment(null);
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingAppointment(null);
    setError(null);
    setPrefillDate(null);
  };

  const getEditInitialData = (appointment) => {
    if (!appointment) return null;
    const apptDate = new Date(appointment?.appointment_date);
    const localDatetime = new Date(apptDate.getTime() - apptDate.getTimezoneOffset() * 60000)?.toISOString()?.slice(0, 16);
    return {
      doctorName: appointment?.doctor_name,
      specialty: appointment?.specialty,
      appointmentDatetime: localDatetime,
      location: appointment?.location,
      notes: appointment?.notes
    };
  };

  const canAddMore = appointments?.length < 3;

  return (
    <>
      <Helmet>
        <title>My Appointments | MyBodyToday</title>
        <meta name="description" content="Manage your doctor appointments and receive reminders" />
      </Helmet>
      <div className="min-h-screen bg-background pb-24">
        <UserProfileHeader />

        <div className="bg-card border-b border-border px-4 py-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
              </button>
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground">My Appointments</h1>
                <p className="text-xs text-muted-foreground">{appointments?.length}/3 upcoming appointments</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex items-center bg-muted rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                    viewMode === 'list' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label="List view"
                >
                  <Icon name="List" size={14} />
                  <span className="hidden sm:inline">List</span>
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                    viewMode === 'calendar' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label="Calendar view"
                >
                  <Icon name="CalendarDays" size={14} />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
              </div>
              {canAddMore && !showForm && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => { setShowForm(true); setEditingAppointment(null); setPrefillDate(null); }}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {successMessage && (
            <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-xl">
              <Icon name="CheckCircle2" size={18} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success-foreground">{successMessage}</p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/20 rounded-xl">
              <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-error">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-error">
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {showForm && (
            <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="CalendarPlus" size={20} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">
                  {editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
                </h2>
              </div>
              <AppointmentForm
                onSubmit={handleAddAppointment}
                onCancel={handleCancelForm}
                initialData={getEditInitialData(editingAppointment)}
                prefillNotes={!editingAppointment ? prefillNotes : undefined}
                prefillDate={!editingAppointment ? prefillDate : undefined}
                isSubmitting={isSubmitting}
              />
            </div>
          )}

          {viewMode === 'list' ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-foreground">Upcoming Appointments</h2>
                {!canAddMore && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">Max 3 reached</span>
                )}
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2]?.map(i => (
                    <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
                      <div className="flex gap-3 mb-3">
                        <div className="w-10 h-10 bg-muted rounded-full" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted rounded w-1/2" />
                          <div className="h-3 bg-muted rounded w-1/3" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : appointments?.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CalendarHeart" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">No upcoming appointments</h3>
                  <p className="text-sm text-muted-foreground mb-5 max-w-xs mx-auto">
                    Add your first doctor appointment to get reminders and prepare your consultation summary.
                  </p>
                  {!showForm && (
                    <Button variant="default" onClick={() => setShowForm(true)} iconName="Plus" iconPosition="left">
                      Add First Appointment
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments?.map(appt => (
                    <AppointmentCard
                      key={appt?.id}
                      appointment={appt}
                      onEdit={handleEditWithData}
                      onDelete={(id) => setDeleteConfirmId(id)}
                    />
                  ))}
                  {canAddMore && !showForm && (
                    <button
                      onClick={() => { setShowForm(true); setEditingAppointment(null); }}
                      className="w-full border-2 border-dashed border-border rounded-xl p-4 flex items-center justify-center gap-2 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
                    >
                      <Icon name="Plus" size={18} />
                      <span className="text-sm font-medium">Add another appointment ({3 - appointments?.length} remaining)</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-foreground">Calendar View</h2>
                {!canAddMore && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">Max 3 reached</span>
                )}
              </div>
              {loading ? (
                <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
                  <div className="h-64 bg-muted rounded-lg" />
                </div>
              ) : (
                <AppointmentCalendarView
                  appointments={appointments}
                  onEdit={handleEditWithData}
                  onDelete={(id) => setDeleteConfirmId(id)}
                  onAddForDate={canAddMore ? handleAddForDate : undefined}
                />
              )}
            </div>
          )}

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="Bell" size={18} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">Automatic Reminders</h3>
                <p className="text-xs text-muted-foreground">You will receive email reminders at 3 key times before each appointment:</p>
                <ul className="mt-2 space-y-1">
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span><strong>7 days before</strong> — Start tracking symptoms</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span><strong>1 day before</strong> — Generate consultation summary</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span><strong>2 hours before</strong> — Summary ready to share</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-error" />
              </div>
              <h3 className="font-semibold text-foreground">Delete Appointment?</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              This will permanently remove the appointment and cancel all scheduled reminders.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleDeleteConfirm}
                className="flex-1 bg-error hover:bg-error/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <BottomTabNavigation />
      <ActionButtonOverlay />
    </>
  );
};

export default DoctorAppointments;
